import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Plus, Trash2, Save, RefreshCw, CalendarDays, X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format, addDays, parseISO } from 'date-fns';

const API = '/api';

const DEFAULT_SLOTS = ["09:00", "09:45", "10:30", "11:15", "12:00", "13:00", "13:45", "14:30", "15:15", "16:00", "16:45", "17:30", "18:00", "18:45", "19:30"];

const AvailabilityTab = () => {
  const [availability, setAvailability] = useState({});
  const [dateOverrides, setDateOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedDay, setSelectedDay] = useState('monday');
  const [newSlot, setNewSlot] = useState('');
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  
  // Date override form state
  const [showOverrideForm, setShowOverrideForm] = useState(false);
  const [overrideDate, setOverrideDate] = useState('');
  const [overrideSlots, setOverrideSlots] = useState([]);
  const [overrideNote, setOverrideNote] = useState('');
  const [overrideNewSlot, setOverrideNewSlot] = useState('');
  const [editingOverride, setEditingOverride] = useState(null);

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  useEffect(() => {
    fetchAvailability();
    fetchDateOverrides();
    fetchUpcomingAppointments();
  }, []);

  const fetchAvailability = async () => {
    try {
      const response = await axios.get(`${API}/availability`);
      const scheduleData = response.data.schedule || [];
      const availObj = {};
      scheduleData.forEach(item => {
        availObj[item.day] = item.slots || [];
      });
      days.forEach(day => {
        if (!availObj[day]) availObj[day] = [];
      });
      setAvailability(availObj);
    } catch (error) {
      console.error('Failed to fetch availability:', error);
      const defaultAvail = {};
      days.forEach(day => { defaultAvail[day] = []; });
      setAvailability(defaultAvail);
    } finally {
      setLoading(false);
    }
  };

  const fetchDateOverrides = async () => {
    try {
      const response = await axios.get(`${API}/availability/overrides`);
      setDateOverrides(response.data.overrides || []);
    } catch (error) {
      console.error('Failed to fetch date overrides:', error);
    }
  };

  const fetchUpcomingAppointments = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await axios.get(`${API}/appointments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const upcoming = response.data.filter(a => a.status === 'scheduled');
      setUpcomingAppointments(upcoming);
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
    }
  };

  const handleSaveAvailability = async () => {
    setSaving(true);
    const token = localStorage.getItem('admin_token');
    try {
      const schedule = days.map(day => ({
        day,
        slots: availability[day] || []
      }));
      
      await axios.put(`${API}/availability`, { schedule }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Weekly availability saved successfully!');
    } catch (error) {
      toast.error('Failed to save availability');
    } finally {
      setSaving(false);
    }
  };

  const addSlot = (day) => {
    if (!newSlot) {
      toast.error('Please enter a time slot');
      return;
    }
    const timeRegex = /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/;
    if (!timeRegex.test(newSlot)) {
      toast.error('Please enter a valid time (e.g., 09:00, 14:30)');
      return;
    }
    
    if (availability[day]?.includes(newSlot)) {
      toast.error('This slot already exists');
      return;
    }
    
    const updatedSlots = [...(availability[day] || []), newSlot].sort();
    setAvailability({ ...availability, [day]: updatedSlots });
    setNewSlot('');
    toast.success(`Added ${newSlot} to ${day}`);
  };

  const removeSlot = (day, slot) => {
    const updatedSlots = availability[day].filter(s => s !== slot);
    setAvailability({ ...availability, [day]: updatedSlots });
  };

  const copyToAllDays = (sourceDay) => {
    const sourceSlots = availability[sourceDay] || [];
    const newAvail = { ...availability };
    days.forEach(day => {
      newAvail[day] = [...sourceSlots];
    });
    setAvailability(newAvail);
    toast.success(`Copied ${sourceDay}'s availability to all days`);
  };

  const addDefaultSlots = (day) => {
    setAvailability({ ...availability, [day]: [...DEFAULT_SLOTS] });
    toast.success(`Added default slots to ${day}`);
  };

  const clearDay = (day) => {
    setAvailability({ ...availability, [day]: [] });
    toast.success(`Cleared all slots for ${day}`);
  };

  // Date Override Functions
  const handleAddOverrideSlot = () => {
    if (!overrideNewSlot) return;
    const timeRegex = /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/;
    if (!timeRegex.test(overrideNewSlot)) {
      toast.error('Please enter a valid time (e.g., 09:00, 14:30)');
      return;
    }
    if (overrideSlots.includes(overrideNewSlot)) {
      toast.error('This slot already exists');
      return;
    }
    setOverrideSlots([...overrideSlots, overrideNewSlot].sort());
    setOverrideNewSlot('');
  };

  const handleRemoveOverrideSlot = (slot) => {
    setOverrideSlots(overrideSlots.filter(s => s !== slot));
  };

  const handleSaveOverride = async () => {
    if (!overrideDate) {
      toast.error('Please select a date');
      return;
    }
    
    const token = localStorage.getItem('admin_token');
    try {
      await axios.post(`${API}/availability/overrides`, {
        date: overrideDate,
        slots: overrideSlots,
        note: overrideNote
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Availability for ${overrideDate} saved!`);
      fetchDateOverrides();
      resetOverrideForm();
    } catch (error) {
      toast.error('Failed to save date override');
    }
  };

  const handleEditOverride = (override) => {
    setEditingOverride(override.date);
    setOverrideDate(override.date);
    setOverrideSlots(override.slots || []);
    setOverrideNote(override.note || '');
    setShowOverrideForm(true);
  };

  const handleDeleteOverride = async (date) => {
    if (!window.confirm(`Delete the override for ${date}? This will revert to the weekly schedule.`)) return;
    
    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/availability/overrides/${date}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Override for ${date} deleted`);
      fetchDateOverrides();
    } catch (error) {
      toast.error('Failed to delete override');
    }
  };

  const resetOverrideForm = () => {
    setShowOverrideForm(false);
    setOverrideDate('');
    setOverrideSlots([]);
    setOverrideNote('');
    setOverrideNewSlot('');
    setEditingOverride(null);
  };

  const copyWeeklyToOverride = () => {
    if (!overrideDate) {
      toast.error('Please select a date first');
      return;
    }
    const dateObj = new Date(overrideDate + 'T12:00:00');
    const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    const weeklySlots = availability[dayName] || [];
    setOverrideSlots([...weeklySlots]);
    toast.success(`Copied ${dayName}'s weekly slots`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF4500]"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#FF4500]" />
            Calendar Availability Management
          </h3>
          <p className="text-sm text-[#525252] mt-1">
            Set your weekly schedule and add date-specific overrides for holidays or special days.
          </p>
        </div>
        <button
          onClick={handleSaveAvailability}
          disabled={saving}
          className="flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 rounded-lg hover:bg-[#1EBE5A] transition-all font-semibold disabled:opacity-50"
        >
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Weekly Schedule
            </>
          )}
        </button>
      </div>

      {/* Date-Specific Overrides Section */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-purple-800 flex items-center gap-2">
            <CalendarDays className="w-5 h-5" />
            Date-Specific Overrides
          </h4>
          <button
            onClick={() => {
              resetOverrideForm();
              setShowOverrideForm(true);
            }}
            className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-all font-semibold text-sm"
          >
            <Plus className="w-4 h-4" />
            Add Date Override
          </button>
        </div>

        <p className="text-sm text-purple-700 mb-4">
          Override your weekly schedule for specific dates. Useful for holidays, vacations, or days with special hours.
        </p>

        {/* Override Form */}
        {showOverrideForm && (
          <div className="bg-white border border-purple-300 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-4">
              <h5 className="font-semibold text-purple-800">
                {editingOverride ? `Edit Override: ${editingOverride}` : 'Add New Date Override'}
              </h5>
              <button onClick={resetOverrideForm} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={overrideDate}
                  onChange={(e) => setOverrideDate(e.target.value)}
                  min={format(new Date(), 'yyyy-MM-dd')}
                  disabled={editingOverride}
                  className="w-full h-10 px-4 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note (optional)</label>
                <input
                  type="text"
                  value={overrideNote}
                  onChange={(e) => setOverrideNote(e.target.value)}
                  placeholder="e.g., Holiday, Vacation, Half day"
                  className="w-full h-10 px-4 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            {/* Time Slots for Override */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">
                  Time Slots <span className="text-gray-500">(leave empty to mark as unavailable)</span>
                </label>
                <button
                  onClick={copyWeeklyToOverride}
                  className="text-xs px-3 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-all"
                >
                  Copy from Weekly Schedule
                </button>
              </div>
              
              <div className="flex gap-2 mb-3">
                <input
                  type="time"
                  value={overrideNewSlot}
                  onChange={(e) => setOverrideNewSlot(e.target.value)}
                  className="h-10 px-4 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <button
                  onClick={handleAddOverrideSlot}
                  className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-all font-semibold text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Add
                </button>
              </div>

              {overrideSlots.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {overrideSlots.map((slot) => (
                    <div
                      key={slot}
                      className="flex items-center gap-2 bg-purple-100 border border-purple-200 rounded-lg px-3 py-1"
                    >
                      <span className="text-sm font-medium text-purple-800">{slot}</span>
                      <button
                        onClick={() => handleRemoveOverrideSlot(slot)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-sm">No slots = Day marked as <strong>UNAVAILABLE</strong></span>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={resetOverrideForm}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all font-semibold text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveOverride}
                className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-all font-semibold text-sm"
              >
                <Save className="w-4 h-4" />
                Save Override
              </button>
            </div>
          </div>
        )}

        {/* List of Existing Overrides */}
        {dateOverrides.length > 0 ? (
          <div className="space-y-2">
            {dateOverrides.map((override) => {
              const isUnavailable = !override.slots || override.slots.length === 0;
              const dateObj = parseISO(override.date);
              const isPast = dateObj < new Date();
              
              return (
                <div
                  key={override.date}
                  className={`flex items-center justify-between bg-white rounded-lg px-4 py-3 border ${
                    isPast ? 'border-gray-200 opacity-60' : isUnavailable ? 'border-red-200' : 'border-purple-200'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="font-semibold text-gray-800">
                        {format(dateObj, 'EEEE, MMMM d, yyyy')}
                      </p>
                      {override.note && (
                        <p className="text-xs text-gray-500">{override.note}</p>
                      )}
                    </div>
                    <div>
                      {isUnavailable ? (
                        <span className="inline-block px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold">
                          UNAVAILABLE
                        </span>
                      ) : (
                        <span className="inline-block px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-semibold">
                          {override.slots.length} slots
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!isPast && (
                      <>
                        <button
                          onClick={() => handleEditOverride(override)}
                          className="text-xs px-3 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-all"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteOverride(override.date)}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-center text-purple-600 py-4">
            No date overrides set. Your weekly schedule applies to all dates.
          </p>
        )}
      </div>

      {/* Upcoming Appointments Quick View */}
      {upcomingAppointments.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Upcoming Scheduled Appointments ({upcomingAppointments.length})
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {upcomingAppointments.slice(0, 6).map((appt) => (
              <div key={appt.id} className="bg-white rounded-lg p-3 border border-blue-100">
                <p className="font-semibold text-sm">{appt.lead_name}</p>
                <p className="text-xs text-blue-600">{appt.date} at {appt.time} EST</p>
                <p className="text-xs text-gray-500">{appt.booking_type === 'next_steps_call' ? 'Next Steps' : 'First Inquiry'}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Weekly Schedule Section */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
        <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-[#FF4500]" />
          Weekly Schedule (Default)
        </h4>

        {/* Day Selector Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {days.map((day) => (
            <button
              key={day}
              onClick={() => setSelectedDay(day)}
              className={`px-4 py-2 rounded-lg font-semibold capitalize transition-all ${
                selectedDay === day
                  ? 'bg-[#FF4500] text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {day}
              <span className="ml-2 text-xs opacity-75">({availability[day]?.length || 0})</span>
            </button>
          ))}
        </div>

        {/* Selected Day Editor */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-bold capitalize">{selectedDay}'s Availability</h4>
            <div className="flex gap-2">
              <button
                onClick={() => addDefaultSlots(selectedDay)}
                className="text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-all"
              >
                Add Default Slots
              </button>
              <button
                onClick={() => copyToAllDays(selectedDay)}
                className="text-xs px-3 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-all"
              >
                Copy to All Days
              </button>
              <button
                onClick={() => clearDay(selectedDay)}
                className="text-xs px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-all"
              >
                Clear Day
              </button>
            </div>
          </div>

          {/* Add New Slot */}
          <div className="flex gap-3 mb-6">
            <input
              type="time"
              value={newSlot}
              onChange={(e) => setNewSlot(e.target.value)}
              className="h-10 px-4 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
            />
            <button
              onClick={() => addSlot(selectedDay)}
              className="flex items-center gap-2 bg-[#FF4500] text-white px-4 py-2 rounded-lg hover:bg-[#E03E00] transition-all font-semibold"
            >
              <Plus className="w-4 h-4" />
              Add Slot
            </button>
          </div>

          {/* Time Slots Grid */}
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
            {(availability[selectedDay] || []).length > 0 ? (
              availability[selectedDay].map((slot) => (
                <div
                  key={slot}
                  className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2"
                >
                  <span className="text-sm font-medium text-green-800">{slot}</span>
                  <button
                    onClick={() => removeSlot(selectedDay, slot)}
                    className="text-red-500 hover:text-red-700 ml-2"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))
            ) : (
              <p className="col-span-full text-center text-gray-500 py-8">
                No time slots set for {selectedDay}. Add slots above or click "Add Default Slots".
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Weekly Overview */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
        <h4 className="font-semibold mb-4">Weekly Overview</h4>
        <div className="grid grid-cols-7 gap-2 text-center">
          {days.map((day) => (
            <div key={day} className="bg-white rounded-lg p-3 border border-gray-100">
              <p className="text-xs font-semibold text-gray-600 capitalize">{day.slice(0, 3)}</p>
              <p className="text-2xl font-bold text-[#FF4500]">{availability[day]?.length || 0}</p>
              <p className="text-xs text-gray-500">slots</p>
            </div>
          ))}
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h4 className="font-semibold text-yellow-800 mb-2">How to Use</h4>
        <ul className="text-sm text-yellow-700 space-y-1">
          <li>• <strong>Weekly Schedule:</strong> Sets your default availability for each day of the week</li>
          <li>• <strong>Date Overrides:</strong> Override specific dates (holidays, vacations, special hours)</li>
          <li>• Leave a date override with <strong>no slots</strong> to mark that day as unavailable</li>
          <li>• Don't forget to click <strong>"Save Weekly Schedule"</strong> for weekly changes!</li>
          <li>• Date overrides are saved immediately when you click "Save Override"</li>
          <li>• All times are in <strong>EST (Eastern Standard Time)</strong></li>
        </ul>
      </div>
    </div>
  );
};

export default AvailabilityTab;
